import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface PrivateRouteProps {
  children: React.ReactNode;
}

export function PrivateRoute({ children }: PrivateRouteProps) {
  const { user, loading } = useAuthContext();
  const location = useLocation();

  useEffect(() => {
    console.log("[PrivateRoute] Current state:", {
      user: user?.id,
      loading,
      pathname: location.pathname,
      redirectAfterAuth: localStorage.getItem('redirectAfterAuth'),
      pendingRelationId: localStorage.getItem('pendingRelationId'),
      referralCode: localStorage.getItem('referral_code')
    });

    if (user) {
      // Gérer la redirection depuis l'authentification
      const urlParams = new URLSearchParams(window.location.search);
      const redirectPath = urlParams.get('redirect_path');
      const relationId = urlParams.get('relation_id');
      
      // Extraire les paramètres de l'URL en mode hash
      const hashPart = window.location.hash;
      const queryIndex = hashPart.indexOf('?');
      let urlReferralCode = null;
      
      if (queryIndex !== -1) {
        const queryPart = hashPart.substring(queryIndex + 1);
        const hashParams = new URLSearchParams(queryPart);
        urlReferralCode = hashParams.get('ref');
      } else {
        // Essayer avec window.location.search comme backup
        urlReferralCode = urlParams.get('ref');
      }
      
      // Capturer le code de parrainage s'il est présent dans l'URL
      if (urlReferralCode) {
        console.log("[PrivateRoute] Found referral code in URL:", urlReferralCode);
        localStorage.setItem('referral_code', urlReferralCode);
      }
      
      if (redirectPath) {
        const decodedPath = decodeURIComponent(redirectPath);
        console.log("[PrivateRoute] Found redirect_path in URL:", decodedPath);
        localStorage.setItem('redirectAfterAuth', decodedPath);
      }

      if (relationId) {
        console.log("[PrivateRoute] Found relation_id in URL:", relationId);
        localStorage.setItem('pendingRelationId', relationId);
      }

      const storedRedirectUrl = localStorage.getItem('redirectAfterAuth');
      const pendingRelationId = localStorage.getItem('pendingRelationId');
      
      // Nettoyer les paramètres d'URL après les avoir traités
      if ((redirectPath || relationId) && window.history.replaceState) {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
      
      // Gérer la redirection vers la page de partage (priorité la plus haute)
      if (pendingRelationId) {
        const shareUrl = `/share/${pendingRelationId}`;
        
        if (location.pathname === shareUrl) {
          console.log("[PrivateRoute] Already on share page, cleaning up after delay");
          setTimeout(() => {
            if (location.pathname === shareUrl) {
              localStorage.removeItem('pendingRelationId');
              localStorage.removeItem('redirectAfterAuth');
            }
          }, 5000);
        } else {
          console.log("[PrivateRoute] Redirecting to share page:", shareUrl);
          window.location.replace(`${window.location.origin}/#${shareUrl}`);
        }
        return;
      }
      
      // Vérifier si l'utilisateur est nouveau en consultant la base de données
      const checkIfNewUser = async () => {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('is_new_user')
            .eq('id', user.id)
            .single();
            
          if (!error && data && data.is_new_user && location.pathname !== '/signup-flow') {
            console.log("[PrivateRoute] User is marked as new in database, redirecting to signup flow");
            window.location.replace(`${window.location.origin}/#/signup-flow`);
            return true;
          }

          // Vérification supplémentaire avec localStorage comme backup
          const isNewUserFromStorage = localStorage.getItem('isNewUser') === 'true';
          if (isNewUserFromStorage && location.pathname !== '/signup-flow') {
            console.log("[PrivateRoute] New user detected from localStorage, redirecting to signup flow");
            window.location.replace(`${window.location.origin}/#/signup-flow`);
            return true;
          }
          
          return false;
        } catch (err) {
          console.error("[PrivateRoute] Error checking if user is new:", err);
          // Fallback au localStorage
          const isNewUserFromStorage = localStorage.getItem('isNewUser') === 'true';
          if (isNewUserFromStorage && location.pathname !== '/signup-flow') {
            console.log("[PrivateRoute] New user detected from localStorage, redirecting to signup flow");
            window.location.replace(`${window.location.origin}/#/signup-flow`);
            return true;
          }
          return false;
        }
      };
      
      // Exécuter la vérification et continuer avec les autres redirections si nécessaire
      checkIfNewUser().then(isNewUserRedirected => {
        if (isNewUserRedirected) return;
        
      // Gérer la redirection normale après authentification
        if (storedRedirectUrl && !localStorage.getItem('redirectionInProgress')) {
        console.log("[PrivateRoute] Redirecting to:", storedRedirectUrl);
        localStorage.setItem('redirectionInProgress', 'true');
        window.location.replace(`${window.location.origin}/#${storedRedirectUrl}`);
      }
      });
    }
  }, [user, location.pathname]);

  // Attendre que le chargement soit terminé
  if (loading) {
    console.log("[PrivateRoute] Loading...");
    return (
      <div 
        className="min-h-screen bg-black flex items-center justify-center"
        role="status"
        aria-label="Chargement en cours"
      >
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-4 border-primary-end/20"></div>
          <div 
            className="absolute inset-0 rounded-full border-4 border-t-primary-end animate-spin"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log("[PrivateRoute] No user, redirecting to login");
    // Stocker le chemin actuel pour redirection après connexion
    const fullPath = location.pathname;
    
    // Vérifier si nous sommes déjà dans une boucle de redirection
    const currentRedirect = localStorage.getItem('redirectAfterAuth');
    const redirectCount = sessionStorage.getItem('redirection_attempt_count');
    
    if (redirectCount && parseInt(redirectCount) > 3) {
      // Nettoyer tous les flags de redirection pour sortir de la boucle
      console.log("[PrivateRoute] Détection d'une boucle de redirection, nettoyage des drapeaux");
      sessionStorage.removeItem('redirection_attempt_count');
      localStorage.removeItem('redirectAfterAuth');
      localStorage.removeItem('redirectionInProgress');
      localStorage.removeItem('pendingRelationId');
    } else if (fullPath && fullPath !== '/' && fullPath !== '/login' && !currentRedirect) {
      // Ne stocker le chemin que s'il est significatif et n'existe pas déjà
      localStorage.setItem('redirectAfterAuth', fullPath);
    }
    
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  console.log("[PrivateRoute] Rendering protected content");
  return <>{children}</>;
}